<template>
	<fieldset class="number-range-filter">
		<div v-if="filter.label" class="number-range-filter__header">
			<legend class="number-range-filter__label">
				<span v-text="filter.label" />
			</legend>
		</div>
		<div class="number-range-filter__field">
			<KtFieldNumber
				v-bind="minFieldProps"
				@blur="onMinInputBlur"
				@update:modelValue="onMinValueInput"
			/>
			<KtFieldNumber
				v-bind="maxFieldProps"
				@blur="onMaxInputBlur"
				@update:modelValue="onMaxValueInput"
			/>
		</div>
	</fieldset>
</template>

<script lang="ts">
import isEqual from 'lodash/isEqual.js'
import { computed, defineComponent, type PropType, ref, watch } from 'vue'

import KtFieldNumber from '../../../../kotti-field-number/KtFieldNumber.vue'
import type { KottiFieldNumber } from '../../../../kotti-field-number/types'
import { useTranslationNamespace } from '../../../../kotti-i18n/hooks'
import type { KottiStandardTable } from '../../types'
import { getReorderedRange } from '../../utilities/filters'

export default defineComponent({
	name: 'NumberRangeFilter',
	components: {
		KtFieldNumber,
	},
	props: {
		filter: {
			required: true,
			type: Object as PropType<
				KottiStandardTable.FilterInternal & {
					type: KottiStandardTable.FilterType.NUMBER_RANGE
				}
			>,
		},
		isLoading: { default: false, type: Boolean },
		modelValue: {
			default: () => [null, null],
			type: Array as unknown as PropType<[number | null, number | null]>,
		},
	},
	emits: ['update:modelValue'],
	setup(props, { emit }) {
		const translations = useTranslationNamespace('KtStandardTable')

		const range = ref<
			[KottiFieldNumber.ModelValue, KottiFieldNumber.ModelValue]
		>([null, null])

		const reOrderAndEmitRangeValue = () => {
			range.value = getReorderedRange(range.value)

			emit('update:modelValue', {
				id: props.filter.id,
				operation: props.filter.operations[0], // Current filters support only one operation
				value: range.value,
			})
		}

		watch(
			() => props.modelValue,
			(newRange) => {
				if (!isEqual(newRange, range.value))
					range.value = [newRange[0], newRange[1]]
			},
			{ immediate: true },
		)

		return {
			maxFieldProps: computed(() => ({
				dataTest: [props.filter.dataTest, 'max-value-input']
					.filter(Boolean)
					.join('.'),
				decimalPlaces: props.filter.decimalPlaces,
				hideChangeButtons: true,
				isLoading: props.isLoading,
				isOptional: true,
				modelValue: range.value[1],
				placeholder: translations.value.max,
				prefix: props.filter.unit,
				size: 'small',
			})),
			minFieldProps: computed(() => ({
				dataTest: [props.filter.dataTest, 'min-value-input']
					.filter(Boolean)
					.join('.'),
				decimalPlaces: props.filter.decimalPlaces,
				hideChangeButtons: true,
				isLoading: props.isLoading,
				isOptional: true,
				modelValue: range.value[0],
				placeholder: translations.value.min,
				prefix: props.filter.unit,
				size: 'small',
			})),
			onMaxInputBlur: (newMax: KottiFieldNumber.ModelValue) => {
				if (props.modelValue[1] !== newMax) reOrderAndEmitRangeValue()
			},
			onMaxValueInput: (newMax: KottiFieldNumber.ModelValue) => {
				range.value = [range.value[0], newMax]
			},
			onMinInputBlur: (newMin: KottiFieldNumber.ModelValue) => {
				if (props.modelValue[0] !== newMin) reOrderAndEmitRangeValue()
			},
			onMinValueInput: (newMin: KottiFieldNumber.ModelValue) => {
				range.value = [newMin, range.value[1]]
			},
		}
	},
})
</script>

<style lang="scss" scoped>
.number-range-filter {
	display: flex;
	flex-direction: column;
	gap: var(--unit-2);

	/* overwrite fieldset properties */
	padding: 0;
	margin: 0;
	border: none;

	&__field {
		display: flex;
		gap: var(--unit-4);
		align-items: center;

		:deep(.kt-field) {
			flex: 1 1 auto;
			margin-bottom: 0;
		}
	}

	&__header {
		display: flex;
		font-size: 0.6rem;
	}

	&__label {
		display: flex;
		align-items: center;

		// This emulates the font-size of small kt-field labels
		font-size: 0.9em;
		color: var(--text-02);

		span {
			font-weight: 500;
		}
	}
}
</style>
